import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { SpinnerGap, ArrowLeft } from '@phosphor-icons/react';
import DealInfoPanels from './dealDetail/DealInfoPanels';
import DealStateTimeline from './dealDetail/DealStateTimeline';
import DealIntelligencePanel from './dealDetail/DealIntelligencePanel';

const fmtEUR = (v) => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(v || 0);

const EMPTY_STATE_FORM = {
  ID_Evento: '', ID_Resultado: '', NomePessoa: '', ContactoPessoa: '',
  Observacoes: '', DataInicio: '', DataFim: '', ValorPrevisto: 0, ID_Encomenda: '',
};

const EMPTY_EDIT_STATE = { DataInicio: '', DataFim: '', Observacao: '', ValorPrevisto: 0 };

const toDtLocal = (v) => (v ? String(v).replace(' ', 'T').slice(0, 16) : '');

// Devolve a data/hora **local do browser** no formato aceite por
// ``<input type="datetime-local">`` — `AAAA-MM-DDTHH:MM`. Não usar
// ``toISOString()`` aqui porque devolve UTC e desloca as horas em ±1
// (Portugal está em UTC+1 no verão, o utilizador veria a intervenção
// gravada com hora atrasada em relação ao seu relógio).
const nowLocalDt = (offsetMs = 0) => {
  const d = new Date(Date.now() + offsetMs);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}` +
         `T${p(d.getHours())}:${p(d.getMinutes())}`;
};

export default function DealDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Add-state form state
  const [showAddState, setShowAddState] = useState(false);
  const [events, setEvents] = useState([]);
  const [results, setResults] = useState([]);
  const [stateForm, setStateForm] = useState(EMPTY_STATE_FORM);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);

  // Edit-last-state state
  const [editingStateId, setEditingStateId] = useState(null);
  const [editingState, setEditingState] = useState(EMPTY_EDIT_STATE);
  const [editingLowerBound, setEditingLowerBound] = useState('');
  const [savingDate, setSavingDate] = useState(false);
  const [editSaveMsg, setEditSaveMsg] = useState(null);

  // Email preview modal
  const [emailPreview, setEmailPreview] = useState(null); // { subject, html_body, ... } | null
  const [emailPreviewLoading, setEmailPreviewLoading] = useState(false);
  const [emailPreviewError, setEmailPreviewError] = useState(null);

  const openEmailPreview = async (auditId) => {
    setEmailPreview(null);
    setEmailPreviewError(null);
    setEmailPreviewLoading(true);
    try {
      const r = await api.get(`/negocios/${id}/emails/${auditId}`);
      setEmailPreview(r.data || null);
    } catch (err) {
      setEmailPreviewError(err?.response?.data?.detail || err.message || 'Não foi possível carregar o email');
      setEmailPreview({}); // ensure modal opens even on error
    } finally {
      setEmailPreviewLoading(false);
    }
  };
  const closeEmailPreview = () => {
    setEmailPreview(null);
    setEmailPreviewError(null);
  };

  const fetchData = useCallback(() => {
    setLoading(true);
    api.post('/negocios/fullDetail', { ID_Negocio: id })
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Add-state handlers ──
  const openAddState = async () => {
    setShowAddState(true);
    setSaveMsg(null);
    const now = nowLocalDt();
    const later = nowLocalDt(15 * 60000);
    setStateForm({ ...EMPTY_STATE_FORM, DataInicio: now, DataFim: later });
    try {
      const [evR, resR] = await Promise.all([
        api.post('/leads/nextEvents', { ID_Negocio: id }),
        api.post('/leads/results'),
      ]);
      setEvents(evR.data || []);
      setResults(resR.data || []);
    } catch (e) { console.error(e); }
  };

  const handleSaveState = async () => {
    if (!stateForm.ID_Evento || !stateForm.ID_Resultado) {
      setSaveMsg('Selecione Tipo de Evento e Resultado');
      return;
    }
    setSaving(true);
    setSaveMsg(null);
    try {
      const res = await api.post(`/negocios/${id}/addState`, stateForm);
      if (res.data?.success) {
        setShowAddState(false);
        setSaveMsg(null);
        fetchData();
      } else {
        setSaveMsg(res.data?.message || 'Erro ao registar estado');
      }
    } catch (e) {
      setSaveMsg('Erro de comunicação');
    }
    setSaving(false);
  };

  // ── Edit-last-state handlers ──
  const startEditLast = (estado) => {
    setEditingStateId(estado.ID_NegocioEstadoNegocio);
    setEditingState({
      DataInicio: toDtLocal(estado.DataInicio || estado.DataExecucao || estado.DataRegisto),
      DataFim: toDtLocal(estado.DataFim || estado.DataExecucao || ''),
      Observacao: estado.Observacao || '',
      ValorPrevisto: data?.Detalhes?.ValorPrevisto || 0,
    });
    // Lower bound = end/exec/start of previous state, so DataInicio can't regress.
    const idx = (data?.EstadosNegocios || []).findIndex((x) => x.ID_NegocioEstadoNegocio === estado.ID_NegocioEstadoNegocio);
    const prev = idx > 0 ? data.EstadosNegocios[idx - 1] : null;
    const lb = prev ? (prev.DataFim || prev.DataExecucao || prev.DataInicio || prev.DataRegisto) : '';
    setEditingLowerBound(toDtLocal(lb));
    setEditSaveMsg(null);
  };

  const cancelEditLast = () => {
    setEditingStateId(null);
    setEditingState(EMPTY_EDIT_STATE);
    setEditingLowerBound('');
    setEditSaveMsg(null);
  };

  const saveEditLast = async (stateId) => {
    const di = editingState.DataInicio;
    const df = editingState.DataFim;
    if (!di) { setEditSaveMsg('Indica a Data Início'); return; }
    if (df && df < di) { setEditSaveMsg('Data Fim não pode ser anterior à Data Início'); return; }
    if (editingLowerBound && di < editingLowerBound) {
      setEditSaveMsg(`Data Início tem de ser posterior ao fim do estado anterior (${editingLowerBound.replace('T', ' ')})`);
      return;
    }
    setSavingDate(true);
    setEditSaveMsg(null);
    try {
      const res = await api.post(`/negocios/state/${stateId}/updateLast`, {
        DataInicio: di ? di.replace('T', ' ') + ':00' : '',
        DataFim: df ? df.replace('T', ' ') + ':00' : '',
        Observacao: editingState.Observacao || '',
        ValorPrevisto: editingState.ValorPrevisto || 0,
      });
      if (res.data?.success) {
        cancelEditLast();
        fetchData();
      } else {
        setEditSaveMsg(res.data?.message || 'Erro ao guardar');
      }
    } catch (e) {
      console.error("Save last state failed", e);
      setEditSaveMsg('Erro de comunicação');
    }
    setSavingDate(false);
  };

  if (loading) return <div className="flex justify-center py-20"><SpinnerGap size={28} className="animate-spin text-[#0F1829]" /></div>;
  if (!data?.Detalhes) return <div className="text-center py-20 text-[#6B7280]">Negócio não encontrado</div>;

  const d = data.Detalhes;
  const encomendas = data.Encomendas || [];
  const emailsCliente = data.EmailsCliente || [];

  return (
    <div data-testid="deal-detail-page" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-[#0F1829] hover:underline mb-4">
        <ArrowLeft size={14} /> Voltar
      </button>
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-[#111827]" style={{ fontFamily: "'Fraunces', sans-serif" }}>Negócio #{id}</h1>
        <p className="text-sm text-[#6B7280] mt-1">{d.TipoNegocio} · {d.Origem || '-'}</p>
      </div>

      <DealInfoPanels detalhes={d} contactosCliente={data.ContactosCliente} />

      <DealIntelligencePanel idNegocio={id} />

      <DealStateTimeline
        states={data.EstadosNegocios}
        showAddState={showAddState}
        stateForm={stateForm}
        setStateForm={setStateForm}
        events={events}
        results={results}
        saving={saving}
        saveMsg={saveMsg}
        onOpenAddState={openAddState}
        onSaveAddState={handleSaveState}
        onCancelAddState={() => { setShowAddState(false); setSaveMsg(null); }}
        editingStateId={editingStateId}
        editingState={editingState}
        setEditingState={setEditingState}
        editingLowerBound={editingLowerBound}
        editSaveMsg={editSaveMsg}
        savingDate={savingDate}
        onStartEditLast={startEditLast}
        onSaveEditLast={saveEditLast}
        onCancelEditLast={cancelEditLast}
      />

      {emailsCliente.length > 0 && (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 mb-4" data-testid="deal-emails-cliente">
          <h3 className="text-sm font-semibold text-[#111827] mb-4 pb-3 border-b border-[#E5E7EB]">
            Emails enviados ao cliente ({emailsCliente.length})
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
                  <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-3 py-2">Data</th>
                  <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-3 py-2">Assunto</th>
                  <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-3 py-2">Fonte</th>
                  <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-3 py-2">Estado</th>
                  <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-3 py-2 w-[80px]">Ver</th>
                </tr>
              </thead>
              <tbody>
                {emailsCliente.map((e) => {
                  const ok = !!(e.SuccessProcessing && e.CreatedSuccess);
                  const hasPreview = !!e.HasPreview;
                  return (
                    <tr key={e.ID_MoosendLeadsHistory} className="border-b border-[#F3F4F6]" data-testid={`deal-email-row-${e.ID_MoosendLeadsHistory}`}>
                      <td className="px-3 py-2 text-sm text-[#374151] whitespace-nowrap">{e.Data}</td>
                      <td className="px-3 py-2 text-sm text-[#111827] max-w-[420px] truncate" title={e.Subject || (e.ErrorMessage || '')}>
                        {e.Subject || (ok ? <span className="text-[#9CA3AF] italic">(sem snapshot)</span> : <span className="text-red-600 truncate">{e.ErrorMessage || '—'}</span>)}
                      </td>
                      <td className="px-3 py-2 text-xs text-[#6B7280]">{e.MailingListId || '—'}</td>
                      <td className="px-3 py-2 text-sm">
                        {ok
                          ? <span className="inline-flex items-center gap-1 text-emerald-700 font-medium">✅ Enviado</span>
                          : <span className="inline-flex items-center gap-1 text-red-600 font-medium">❌ Falha</span>}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {hasPreview ? (
                          <button
                            type="button"
                            onClick={() => openEmailPreview(e.ID_MoosendLeadsHistory)}
                            data-testid={`deal-email-view-${e.ID_MoosendLeadsHistory}`}
                            className="px-2 py-1 rounded border border-[#E5E7EB] text-[#0F1829] hover:bg-[#F3F4F6] font-medium"
                          >Ver</button>
                        ) : (
                          <span className="text-[#9CA3AF]" title="Envios anteriores ao fix (2026-07-06) não têm snapshot arquivado.">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-[11px] text-[#6B7280]">
            O conteúdo do email é acrescentado à observação do estado "Contacto Inicial" no momento do envio.
          </p>
        </div>
      )}

      {/* Modal de pré-visualização do email */}
      {(emailPreview !== null || emailPreviewLoading) && (
        <div
          className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-4"
          data-testid="deal-email-preview-modal"
          onClick={closeEmailPreview}
        >
          <div
            className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-[#E5E7EB]">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] font-semibold mb-1">Email enviado ao cliente</p>
                <h3 className="text-sm font-semibold text-[#111827] truncate" title={emailPreview?.Subject}>
                  {emailPreview?.Subject || (emailPreviewLoading ? 'A carregar…' : 'Sem assunto')}
                </h3>
                {emailPreview?.ToEmail && (
                  <p className="text-xs text-[#6B7280] mt-1">
                    Para: <span className="font-mono">{emailPreview.ToEmail}</span>
                    {emailPreview.SentAt && (<> · {emailPreview.SentAt}</>)}
                  </p>
                )}
              </div>
              <button
                onClick={closeEmailPreview}
                data-testid="deal-email-preview-close"
                className="text-2xl text-[#6B7280] hover:text-[#0F1829] leading-none px-2"
                aria-label="Fechar"
              >×</button>
            </div>
            <div className="flex-1 overflow-auto bg-[#F9FAFB] p-3">
              {emailPreviewLoading && (
                <div className="text-center py-16 text-[#6B7280]"><SpinnerGap size={24} className="animate-spin mx-auto" /></div>
              )}
              {emailPreviewError && (
                <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800" data-testid="deal-email-preview-error">
                  {emailPreviewError}
                </div>
              )}
              {emailPreview?.HtmlBody && (
                <iframe
                  title="Email preview"
                  sandbox=""
                  srcDoc={emailPreview.HtmlBody}
                  className="w-full h-[70vh] bg-white border border-[#E5E7EB] rounded"
                  data-testid="deal-email-preview-iframe"
                />
              )}
            </div>
          </div>
        </div>
      )}

      {encomendas.length > 0 && (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
          <h3 className="text-sm font-semibold text-[#111827] mb-4 pb-3 border-b border-[#E5E7EB]">Encomendas do Contacto</h3>
          <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
              <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-3 py-2">ID</th>
              <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-3 py-2">Estado</th>
              <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-3 py-2">Data</th>
              <th className="text-right text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-3 py-2">Valor</th>
            </tr></thead>
            <tbody>{encomendas.map((e) => (
              <tr key={e.ID_Encomenda} onClick={() => navigate(`/encomendas/${e.ID_Encomenda}`)} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB] cursor-pointer transition-colors">
                <td className="px-3 py-2 text-sm font-mono text-[#0F1829] underline">{e.ID_Encomenda}</td>
                <td className="px-3 py-2 text-sm">{e.DescricaoEstadoEncomenda}</td>
                <td className="px-3 py-2 text-sm text-[#6B7280]">{e.DataEncomenda || '-'}</td>
                <td className="px-3 py-2 text-sm font-mono text-right">{fmtEUR(e.Valor)}</td>
              </tr>
            ))}</tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}
